import React, { useState } from 'react';
import {
  Box, Button, Checkbox, FormControl, FormControlLabel, Grid, InputLabel, MenuItem, Paper, Select, Stack, Table,
  TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import {
  SoaFormats, SoaOrientation, SoaPageSize, SoaTemplateDefinition, soaContentWidthMm, soaPageSizeMm,
} from '../../types/formattedSoa';
import { TRANS_TYPE_NAMES, examplePattern, formatSampleAmount, round2 } from '../../utils/soaDesigner';
import { ColorField, NumField, SmallIconButton } from './designerInputs';

/** Page, default text, number and date formats, and the document codes printed by the DOCTYPE column. */

type Change = (fn: (d: SoaTemplateDefinition) => SoaTemplateDefinition, mergeKey?: string) => void;

interface Props {
  def: SoaTemplateDefinition;
  onChange: Change;
  fonts: string[];
  description: string;
  onDescription: (v: string) => void;
}

const DATE_RE = /^[A-Za-z0-9 ,./:'_-]{1,40}$/;

const PatternField: React.FC<{ label: string; value: string; onChange: (v: string) => void }> = ({ label, value, onChange }) => {
  const [text, setText] = useState(value);
  const valid = DATE_RE.test(text);
  return (
    <TextField
      size="small"
      label={label}
      value={text}
      error={!valid}
      helperText={valid ? `e.g. ${examplePattern(text)}` : 'Letters, digits, spaces and , . / : \' _ - only'}
      onChange={(e) => {
        setText(e.target.value);
        if (DATE_RE.test(e.target.value)) onChange(e.target.value);
      }}
      sx={{ width: 230 }}
    />
  );
};

const TemplateSettings: React.FC<Props> = ({ def, onChange, fonts, description, onDescription }) => {
  const setPage = (patch: Partial<SoaTemplateDefinition['page']>, key: string) => onChange((d) => ({ ...d, page: { ...d.page, ...patch } }), `page-${key}`);
  const setMargin = (side: 'top' | 'right' | 'bottom' | 'left', v: number) => onChange(
    (d) => ({ ...d, page: { ...d.page, margin: { ...d.page.margin, [side]: v } } }),
    `margin-${side}`,
  );
  const setDefaults = (patch: Partial<SoaTemplateDefinition['defaults']>, key: string) => onChange((d) => ({ ...d, defaults: { ...d.defaults, ...patch } }), `defaults-${key}`);
  const setFormats = (patch: Partial<SoaFormats>, key: string) => onChange((d) => ({ ...d, formats: { ...d.formats, ...patch } }), `formats-${key}`);
  const [newCode, setNewCode] = useState('');
  const size = soaPageSizeMm(def);
  const docTypes = Object.entries(def.docTypes).sort(([a], [b]) => Number(a) - Number(b));
  const setDocTypes = (map: Record<string, string>, key: string) => onChange((d) => ({ ...d, docTypes: map }), `doctypes-${key}`);
  const newCodeValid = /^-?\d{1,10}$/.test(newCode) && !(newCode in def.docTypes);

  return (
    <Box sx={{ p: 2 }}>
      <Grid container spacing={2}>
        <Grid item xs={12} lg={6}>
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>Template</Typography>
            <TextField size="small" fullWidth multiline minRows={2} label="Description" value={description}
              inputProps={{ maxLength: 500 }} onChange={(e) => onDescription(e.target.value)} />
          </Paper>

          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>Page</Typography>
            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
              <FormControl size="small" sx={{ width: 140 }}>
                <InputLabel>Paper</InputLabel>
                <Select label="Paper" value={def.page.size} onChange={(e) => setPage({ size: e.target.value as SoaPageSize }, 'size')}>
                  <MenuItem value="A4">A4 (210 × 297)</MenuItem>
                  <MenuItem value="Letter">Letter</MenuItem>
                  <MenuItem value="Legal">Legal</MenuItem>
                  <MenuItem value="A5">A5 (148 × 210)</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ width: 150 }}>
                <InputLabel>Orientation</InputLabel>
                <Select label="Orientation" value={def.page.orientation} onChange={(e) => setPage({ orientation: e.target.value as SoaOrientation }, 'orientation')}>
                  <MenuItem value="portrait">Portrait</MenuItem>
                  <MenuItem value="landscape">Landscape</MenuItem>
                </Select>
              </FormControl>
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>Margins</Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {(['top', 'right', 'bottom', 'left'] as const).map((side) => (
                <NumField key={side} label={side[0].toUpperCase() + side.slice(1)} unit="mm" value={def.page.margin[side]} min={0} max={60} width={100}
                  onChange={(v) => setMargin(side, v ?? 0)} />
              ))}
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Page {round2(size.w)} × {round2(size.h)} mm; printable width {round2(soaContentWidthMm(def))} mm. Elements keep their
              positions: check them after changing the paper or the margins.
            </Typography>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>Default text</Typography>
            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap alignItems="center">
              <FormControl size="small" sx={{ width: 190 }}>
                <InputLabel>Font</InputLabel>
                <Select label="Font" value={def.defaults.fontFamily} onChange={(e) => setDefaults({ fontFamily: String(e.target.value) }, 'font')}>
                  {fonts.map((f) => <MenuItem key={f} value={f} sx={{ fontFamily: `"${f}", sans-serif` }}>{f}</MenuItem>)}
                </Select>
              </FormControl>
              <NumField label="Size" unit="pt" value={def.defaults.fontSize} min={4} max={72} width={100} onChange={(v) => setDefaults({ fontSize: v ?? 8 }, 'size')} />
              <NumField label="Line height" value={def.defaults.lineHeight} min={0.8} max={3} step={0.05} width={110} onChange={(v) => setDefaults({ lineHeight: v ?? 1.25 }, 'lineHeight')} />
              <ColorField label="Colour" value={def.defaults.color} onChange={(v) => setDefaults({ color: v || '#000000' }, 'color')} />
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Arial, Times New Roman and Courier New print alike on every server. Arabic text uses the Noto Arabic fonts when the chosen one has none.
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} lg={6}>
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>Dates and amounts</Typography>
            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
              <PatternField key={`d-${def.formats.date}`} label="Date" value={def.formats.date} onChange={(v) => setFormats({ date: v }, 'date')} />
              <PatternField key={`t-${def.formats.dateTime}`} label="Date and time" value={def.formats.dateTime} onChange={(v) => setFormats({ dateTime: v }, 'dateTime')} />
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Tokens: DD D (day) · MM M MMM MMMM (month) · YY YYYY (year) · HH mm ss (time).
            </Typography>
            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap alignItems="center">
              <FormControl size="small" sx={{ width: 200 }}>
                <InputLabel>Decimal places</InputLabel>
                <Select label="Decimal places" value={def.formats.amountDecimals === null ? 'company' : String(def.formats.amountDecimals)}
                  onChange={(e) => setFormats({ amountDecimals: e.target.value === 'company' ? null : Number(e.target.value) }, 'decimals')}>
                  <MenuItem value="company">The company&apos;s setting</MenuItem>
                  {[0, 1, 2, 3, 4, 5, 6].map((n) => <MenuItem key={n} value={String(n)}>{n}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ width: 170 }}>
                <InputLabel>Negative amounts</InputLabel>
                <Select label="Negative amounts" value={def.formats.negative} onChange={(e) => setFormats({ negative: e.target.value as SoaFormats['negative'] }, 'negative')}>
                  <MenuItem value="minus">-1,234.500</MenuItem>
                  <MenuItem value="parentheses">(1,234.500)</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ width: 150 }}>
                <InputLabel>Zero</InputLabel>
                <Select label="Zero" value={def.formats.zero} onChange={(e) => setFormats({ zero: e.target.value as SoaFormats['zero'] }, 'zero')}>
                  <MenuItem value="show">0.000</MenuItem>
                  <MenuItem value="blank">Blank</MenuItem>
                  <MenuItem value="dash">-</MenuItem>
                </Select>
              </FormControl>
              <FormControlLabel
                control={<Checkbox size="small" checked={def.formats.thousands} onChange={(e) => setFormats({ thousands: e.target.checked }, 'thousands')} />}
                label="Thousands separator"
              />
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Example: {formatSampleAmount(-1234567.5, 'amount', def)} · {formatSampleAmount(0, 'amount', def) || '(blank)'} ·{' '}
              {formatSampleAmount(39713.324, 'drcr', def)}. &quot;The company&apos;s setting&quot; is Company Management › amount decimal places (KWD 3).
            </Typography>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>Document codes</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              What the &quot;Document code&quot; column prints for each SAP transaction type (SIV, RV, …). Types not listed print their number.
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>SAP type</TableCell>
                  <TableCell>Document</TableCell>
                  <TableCell>Code printed</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {docTypes.map(([code, label]) => (
                  <TableRow key={code}>
                    <TableCell>{code}</TableCell>
                    <TableCell>{TRANS_TYPE_NAMES[code] ?? 'Other'}</TableCell>
                    <TableCell>
                      <TextField size="small" value={label} inputProps={{ maxLength: 20, 'aria-label': `Code for type ${code}` }} sx={{ width: 110 }}
                        onChange={(e) => setDocTypes({ ...def.docTypes, [code]: e.target.value }, `edit-${code}`)} />
                    </TableCell>
                    <TableCell>
                      <SmallIconButton label={`Remove type ${code}`} color="error"
                        onClick={() => { const next = { ...def.docTypes }; delete next[code]; setDocTypes(next, `remove-${code}`); }}>
                        <DeleteOutlineIcon fontSize="small" />
                      </SmallIconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5 }}>
              <TextField size="small" label="SAP type" value={newCode} onChange={(e) => setNewCode(e.target.value.trim())}
                error={!!newCode && !newCodeValid}
                helperText={newCode && !newCodeValid ? (newCode in def.docTypes ? 'Already listed' : 'A number, e.g. 69') : ' '}
                sx={{ width: 130 }} />
              <Button size="small" startIcon={<AddIcon />} disabled={!newCodeValid}
                onClick={() => { setDocTypes({ ...def.docTypes, [newCode]: TRANS_TYPE_NAMES[newCode]?.slice(0, 3).toUpperCase() ?? newCode }, `add-${newCode}`); setNewCode(''); }}>
                Add type
              </Button>
              {newCode && TRANS_TYPE_NAMES[newCode] && <Typography variant="caption">{TRANS_TYPE_NAMES[newCode]}</Typography>}
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default TemplateSettings;
